import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { AiCredits_PricingResponseDados } from '../contracts/AiCredits_PricingResponse'
import type { AiCredits_PricingInput } from '../contracts/AiCredits_PricingInput'
import type { AiCredits_AgentSettingsResponseDados } from '../contracts/AiCredits_AgentSettingsResponse'
import type { AiCredits_AgentSettingsInput } from '../contracts/AiCredits_AgentSettingsInput'
import type { AiCredits_PegResponseDados } from '../contracts/AiCredits_PegResponse'
import type { AiCredits_PegInput } from '../contracts/AiCredits_PegInput'
import type { AiCredits_OverrideRow } from '../contracts/AiCredits_OverrideRow'
import type { AiCredits_OverridesListResponseDados } from '../contracts/AiCredits_OverridesListResponse'
import type { AiCredits_PegOverrideRow } from '../contracts/AiCredits_PegOverrideRow'
import type { AiCredits_PegOverrideInput } from '../contracts/AiCredits_PegOverrideInput'
import type { AiCredits_PegOverridesListResponseDados } from '../contracts/AiCredits_PegOverridesListResponse'
import type { AiCredits_ReserveSettingsInput } from '../contracts/AiCredits_ReserveSettingsInput'
import type { AiCredits_ReserveSettingsResponseDados } from '../contracts/AiCredits_ReserveSettingsResponse'
import type { AiCredits_PricingHistoryRow } from '../contracts/AiCredits_PricingHistoryRow'
import type { AiCredits_PricingHistoryResponseDados } from '../contracts/AiCredits_PricingHistoryResponse'
import type { AiCredits_PricingHistoryQueryInput } from '../contracts/AiCredits_PricingHistoryQueryInput'

// Import side-effect: registers the contracts with the registry.
import '../contracts/AiCredits_PricingResponse'
import '../contracts/AiCredits_PricingInput'
import '../contracts/AiCredits_AgentSettingsResponse'
import '../contracts/AiCredits_AgentSettingsInput'
import '../contracts/AiCredits_PegResponse'
import '../contracts/AiCredits_PegInput'
import '../contracts/AiCredits_OverridesListResponse'
import '../contracts/AiCredits_RevokeOverrideInput'
import '../contracts/AiCredits_RevokeOverrideResponse'
import '../contracts/AiCredits_PricingHistoryResponse'

/**
 * useAiSettings — SOLE network owner for ai/credits/pricing, ai/agent-settings and
 * ai/credits/peg routes.
 *
 * `subscriptions/billing-settings` (billing_grace_days) was retired from this
 * composable in M6 along with its only consumer, ConfiguracoesIaSettingsForm's
 * "Dias de carência" field — the config key itself and its backend reader
 * (SubscriptionConfirmationService) are untouched, just no longer surfaced here.
 *
 * Owns:
 *   GET  /ai/credits/pricing
 *   POST /ai/credits/pricing          (global versioned row)
 *   POST /ai/credits/pricing/override (per-franchise versioned row)
 *   GET  /ai/credits/pricing/overrides            (active per-franchise overrides listing)
 *   POST /ai/credits/pricing/overrides/{id}/revoke (closes an override's window)
 *   GET  /ai/credits/pricing/history              (published global pricing, newest-first)
 *   GET  /ai/agent-settings
 *   POST /ai/agent-settings
 *   GET  /ai/credits/peg
 *   POST /ai/credits/peg              (global USD-per-credit peg)
 */

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Module-scope singleton state ────────────────────────────────────────────

const currentPricing = ref<AiCredits_PricingResponseDados | null>(null)
const agentSettings = ref<AiCredits_AgentSettingsResponseDados | null>(null)
const peg = ref<AiCredits_PegResponseDados | null>(null)
// Per-franchise peg overrides (2026-08-21). The peg is USD of provider cost per
// credit, so a SMALLER peg burns that franchise's credits FASTER — the lever
// that replaced overriding a franchise's provider COST.
const pegOverrides = ref<AiCredits_PegOverrideRow[]>([])
const savingPegOverride = ref(false)
// The flat-cap admission assumptions — how pessimistic the pre-turn hold is.
const reserveSettings = ref<AiCredits_ReserveSettingsResponseDados | null>(null)
const savingReserveSettings = ref(false)
const overrides = ref<AiCredits_OverrideRow[]>([])
const pricingHistory = ref<AiCredits_PricingHistoryRow[]>([])
const publishingRate = ref(false)
const savingAgentSettings = ref(false)
const savingPeg = ref(false)

// ── Public composable ────────────────────────────────────────────────────────

export function useAiSettings() {
  /**
   * loadCurrentPricing — GET /ai/credits/pricing?model=deepseek-v4-flash.
   * Populates currentPricing ref. On error, logs and rethrows.
   */
  async function loadCurrentPricing(franquiaId?: number): Promise<void> {
    try {
      const params = new URLSearchParams({ model: 'deepseek-v4-flash' })
      if (franquiaId != null) params.set('franquia_id', String(franquiaId))
      const raw = await httpService.get(
        sistemaUrl(`ai/credits/pricing?${params.toString()}`),
        { responseContract: 'AiCredits_PricingResponse' },
      )
      const resp = raw as { dados: AiCredits_PricingResponseDados }
      currentPricing.value = resp.dados
      log('useAiSettings', 'loadCurrentPricing', 'Pricing carregado', resp.dados)
    } catch (err) {
      log('useAiSettings', 'loadCurrentPricing', 'Erro ao carregar pricing', { message: (err as Error)?.message })
      throw err
    }
  }

  /**
   * publishPricingRate — POST /ai/credits/pricing (global) or
   * POST /ai/credits/pricing/override (when franquia_id is set).
   * Creates an immutable versioned row.
   */
  async function publishPricingRate(input: AiCredits_PricingInput): Promise<void> {
    publishingRate.value = true
    try {
      const isOverride = input.franquia_id != null
      const url = isOverride
        ? sistemaUrl('ai/credits/pricing/override')
        : sistemaUrl('ai/credits/pricing')
      await httpService.post(url, input, { inputContract: 'AiCredits_PricingInput', responseContract: 'AiCredits_PricingResponse' })
      log('useAiSettings', 'publishPricingRate', 'Rate publicado', { isOverride, model: input.model_name })
    } catch (err) {
      log('useAiSettings', 'publishPricingRate', 'Erro ao publicar rate', { message: (err as Error)?.message })
      throw err
    } finally {
      publishingRate.value = false
    }
  }

  /**
   * loadOverrides — GET /ai/credits/pricing/overrides.
   * Every per-franchise pricing override currently in force, across all
   * franchises. Populates the overrides ref. On error, logs and rethrows.
   */
  async function loadOverrides(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('ai/credits/pricing/overrides'),
        { responseContract: 'AiCredits_OverridesListResponse' },
      )
      const resp = raw as { dados: AiCredits_OverridesListResponseDados }
      overrides.value = resp.dados.rows
      log('useAiSettings', 'loadOverrides', 'Overrides carregados', { count: overrides.value.length })
    } catch (err) {
      log('useAiSettings', 'loadOverrides', 'Erro ao carregar overrides', { message: (err as Error)?.message })
      throw err
    }
  }

  /**
   * revokeOverride — POST /ai/credits/pricing/overrides/{id}/revoke.
   * Closes the override's window (valid_to = now) — NEVER a hard delete; the
   * revoked row stays in history. Caller is responsible for refreshing the
   * overrides listing afterwards.
   */
  async function revokeOverride(id: number): Promise<void> {
    try {
      await httpService.post(
        sistemaUrl(`ai/credits/pricing/overrides/${id}/revoke`),
        {},
        { inputContract: 'AiCredits_RevokeOverrideInput', responseContract: 'AiCredits_RevokeOverrideResponse' },
      )
      log('useAiSettings', 'revokeOverride', 'Override revogado', { id })
    } catch (err) {
      log('useAiSettings', 'revokeOverride', 'Erro ao revogar override', { message: (err as Error)?.message })
      throw err
    }
  }

  /**
   * loadReserveSettings — GET /ai/credits/reserve-settings.
   * The two token assumptions plus the credit floor they produce at the global
   * peg, so the console can show the consequence rather than raw token counts.
   */
  async function loadReserveSettings(): Promise<void> {
    try {
      const raw = await httpService.get(sistemaUrl('ai/credits/reserve-settings'), {
        responseContract: 'AiCredits_ReserveSettingsResponse',
      })
      reserveSettings.value = (raw as { dados: AiCredits_ReserveSettingsResponseDados }).dados
      log('useAiSettings', 'loadReserveSettings', 'Reserve settings carregadas', reserveSettings.value)
    } catch (err) {
      log('useAiSettings', 'loadReserveSettings', 'Erro ao carregar reserve settings', {
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * saveReserveSettings — POST /ai/credits/reserve-settings.
   * Either field alone is a valid write; the omitted one is left unchanged.
   */
  async function saveReserveSettings(input: AiCredits_ReserveSettingsInput): Promise<void> {
    savingReserveSettings.value = true
    try {
      await httpService.post(sistemaUrl('ai/credits/reserve-settings'), input, {
        inputContract: 'AiCredits_ReserveSettingsInput',
        responseContract: 'AiCredits_ReserveSettingsResponse',
      })
      log('useAiSettings', 'saveReserveSettings', 'Reserve settings salvas', input)
    } catch (err) {
      log('useAiSettings', 'saveReserveSettings', 'Erro ao salvar reserve settings', {
        message: (err as Error)?.message,
      })
      throw err
    } finally {
      savingReserveSettings.value = false
    }
  }

  /**
   * loadPegOverrides — GET /ai/credits/peg-overrides[?franquia_id=].
   *
   * Without franquiaId: every override in force, across all franchises (the
   * staff roster). With one: that franchise's FULL history, closed windows
   * included — the table is insert-only, and a closed row is the evidence for
   * every burn priced under it.
   */
  async function loadPegOverrides(franquiaId?: number): Promise<void> {
    try {
      const url = franquiaId
        ? sistemaUrl(`ai/credits/peg-overrides?franquia_id=${franquiaId}`)
        : sistemaUrl('ai/credits/peg-overrides')
      const raw = await httpService.get(url, {
        responseContract: 'AiCredits_PegOverridesListResponse',
      })
      const resp = raw as { dados: AiCredits_PegOverridesListResponseDados }
      pegOverrides.value = resp.dados.rows
      log('useAiSettings', 'loadPegOverrides', 'Peg overrides carregados', {
        count: pegOverrides.value.length,
        franquiaId,
      })
    } catch (err) {
      log('useAiSettings', 'loadPegOverrides', 'Erro ao carregar peg overrides', {
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * savePegOverride — POST /ai/credits/peg-overrides.
   *
   * Creates a NEW versioned row; the franchise's open window closes at the new
   * valid_from. Never an update.
   *
   * peg_usd travels as a decimal STRING and must stay one all the way down.
   * JavaScript renders any number below 1e-6 in exponential notation
   * (String(0.000000001) === '1e-9'), which the backend contract rejects by
   * name — so a caller that hands this a `number` will be refused, on purpose.
   */
  async function savePegOverride(input: AiCredits_PegOverrideInput): Promise<void> {
    savingPegOverride.value = true
    try {
      await httpService.post(sistemaUrl('ai/credits/peg-overrides'), input, {
        inputContract: 'AiCredits_PegOverrideInput',
        responseContract: 'AiCredits_PegOverrideResponse',
      })
      log('useAiSettings', 'savePegOverride', 'Peg override salvo', {
        franquia_id: input.franquia_id,
      })
    } catch (err) {
      log('useAiSettings', 'savePegOverride', 'Erro ao salvar peg override', {
        message: (err as Error)?.message,
      })
      throw err
    } finally {
      savingPegOverride.value = false
    }
  }

  /**
   * revokePegOverride — POST /ai/credits/peg-overrides/{id}/revoke.
   * Closes the window; the franchise falls back to the global peg from that
   * instant on. Never a delete. Caller refreshes the listing afterwards.
   */
  async function revokePegOverride(id: number): Promise<void> {
    try {
      await httpService.post(
        sistemaUrl(`ai/credits/peg-overrides/${id}/revoke`),
        {},
        {
          inputContract: 'AiCredits_RevokeOverrideInput',
          responseContract: 'AiCredits_RevokeOverrideResponse',
        },
      )
      log('useAiSettings', 'revokePegOverride', 'Peg override revogado', { id })
    } catch (err) {
      log('useAiSettings', 'revokePegOverride', 'Erro ao revogar peg override', {
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * loadPricingHistory — GET /ai/credits/pricing/history?model=&limit=.
   * Published global pricing rows, newest-first. Populates the
   * pricingHistory ref. On error, logs and rethrows.
   */
  async function loadPricingHistory(query: AiCredits_PricingHistoryQueryInput = {}): Promise<void> {
    try {
      const params = new URLSearchParams()
      if (query.model) params.set('model', query.model)
      if (query.limit != null) params.set('limit', String(query.limit))
      const qs = params.toString()
      const raw = await httpService.get(
        sistemaUrl('ai/credits/pricing/history') + (qs ? `?${qs}` : ''),
        { responseContract: 'AiCredits_PricingHistoryResponse' },
      )
      const resp = raw as { dados: AiCredits_PricingHistoryResponseDados }
      pricingHistory.value = resp.dados.rows
      log('useAiSettings', 'loadPricingHistory', 'Histórico carregado', { count: pricingHistory.value.length })
    } catch (err) {
      log('useAiSettings', 'loadPricingHistory', 'Erro ao carregar histórico', { message: (err as Error)?.message })
      throw err
    }
  }

  /**
   * loadAgentSettings — GET /ai/agent-settings.
   * Populates agentSettings ref. On error, logs and rethrows so the caller
   * can degrade the section gracefully.
   */
  async function loadAgentSettings(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('ai/agent-settings'),
        { responseContract: 'AiCredits_AgentSettingsResponse' },
      )
      const resp = raw as { dados: AiCredits_AgentSettingsResponseDados }
      agentSettings.value = resp.dados
      log('useAiSettings', 'loadAgentSettings', 'Agent settings carregadas', resp.dados)
    } catch (err) {
      log('useAiSettings', 'loadAgentSettings', 'Erro ao carregar agent settings', { message: (err as Error)?.message })
      throw err
    }
  }

  /**
   * saveAgentSettings — POST /ai/agent-settings.
   * Sends only the keys provided in `input` (partial update).
   */
  async function saveAgentSettings(input: AiCredits_AgentSettingsInput): Promise<void> {
    savingAgentSettings.value = true
    try {
      await httpService.post(
        sistemaUrl('ai/agent-settings'),
        input,
        { inputContract: 'AiCredits_AgentSettingsInput', responseContract: 'AiCredits_AgentSettingsResponse' },
      )
      log('useAiSettings', 'saveAgentSettings', 'Agent settings salvas', input)
    } catch (err) {
      log('useAiSettings', 'saveAgentSettings', 'Erro ao salvar agent settings', { message: (err as Error)?.message })
      throw err
    } finally {
      savingAgentSettings.value = false
    }
  }

  /**
   * loadPeg — GET /ai/credits/peg.
   * Populates peg ref. On error, logs and rethrows so the caller can
   * degrade the section gracefully.
   */
  async function loadPeg(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('ai/credits/peg'),
        { responseContract: 'AiCredits_PegResponse' },
      )
      const resp = raw as { dados: AiCredits_PegResponseDados }
      peg.value = resp.dados
      log('useAiSettings', 'loadPeg', 'Peg carregado', resp.dados)
    } catch (err) {
      log('useAiSettings', 'loadPeg', 'Erro ao carregar peg', { message: (err as Error)?.message })
      throw err
    }
  }

  /**
   * savePeg — POST /ai/credits/peg.
   */
  async function savePeg(input: AiCredits_PegInput): Promise<void> {
    savingPeg.value = true
    try {
      await httpService.post(
        sistemaUrl('ai/credits/peg'),
        input,
        { inputContract: 'AiCredits_PegInput', responseContract: 'AiCredits_PegResponse' },
      )
      log('useAiSettings', 'savePeg', 'Peg salvo', input)
    } catch (err) {
      log('useAiSettings', 'savePeg', 'Erro ao salvar peg', { message: (err as Error)?.message })
      throw err
    } finally {
      savingPeg.value = false
    }
  }

  /**
   * _reset — wipes module-scope state. Call in afterEach for test isolation.
   */
  function _reset(): void {
    currentPricing.value = null
    agentSettings.value = null
    peg.value = null
    overrides.value = []
    pricingHistory.value = []
    publishingRate.value = false
    savingAgentSettings.value = false
    savingPeg.value = false
  }

  return {
    // state
    currentPricing,
    agentSettings,
    peg,
    overrides,
    pegOverrides,
    savingPegOverride,
    reserveSettings,
    savingReserveSettings,
    pricingHistory,
    publishingRate,
    savingAgentSettings,
    savingPeg,
    // methods
    loadCurrentPricing,
    publishPricingRate,
    loadOverrides,
    revokeOverride,
    loadReserveSettings,
    saveReserveSettings,
    loadPegOverrides,
    savePegOverride,
    revokePegOverride,
    loadPricingHistory,
    loadAgentSettings,
    saveAgentSettings,
    loadPeg,
    savePeg,
    _reset,
  }
}
